import {
    type FC,
    type ReactNode,
    useState,
    useCallback,
    useEffect,
    type MouseEventHandler,
    type Dispatch,
    type SetStateAction,
    lazy,
    Suspense,
} from 'react';
import { graphql, useFragment } from 'react-relay';
import { useSelector } from 'react-redux';
import classnames from 'classnames';

import slugify from 'dibs-slugify/exports/slugify';
import {
    trackEvent,
    eventNameConstants,
    stepInteractionConstants,
    interactionTypeConstants,
} from 'dibs-tracking';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';

import { Link } from 'dibs-elements/exports/Link';
import { TabList } from 'dibs-elements/exports/TabList';
import { TabPanels } from 'dibs-elements/exports/TabPanels';
import { TabProvider } from 'dibs-elements/exports/TabProvider';
import { useTab } from 'dibs-elements/exports/useTab';
import { PdpRespStorytellingTab } from '../PdpRespStorytellingModule/PdpRespStorytellingTab';
import {
    PdpSharedStorytellingTrackingContextProvider,
    PdpSharedStorytellingTrackingWrapper,
} from '../PdpSharedStorytellingComponents/PdpSharedStorytellingTracking';
import { PdpRespDealerDetails } from '../PdpRespDealerDetails/PdpRespDealerDetails';
import { PdpRespShippingReturns } from '../PdpRespDetails/PdpRespShippingReturns';
import { PdpRespTabsCustomizationPanel } from './PdpRespTabsCustomizationPanel';
import { PdpRespSharedAboutItemPanel } from '../respShared/PdpRespSharedAboutItemPanel/PdpRespSharedAboutItemPanel';
import { useShowPricingGuidance } from 'dibs-pricing-guidance/exports/useShowPricingGuidance';
import { usePdpSellerReviewsVariant } from '../helpers/pdpSellerReviewsTestHelpers';
import { getSelectedSku } from '../helpers/getSelectedSku';
import {
    ITEM_DETAILS,
    ABOUT_SELLER,
    CUSTOMIZATION,
    SHIPPING_RETURNS,
    labelMap,
} from '../helpers/tabConstants';
import { type RelayVariables, type Store } from '../../global/relayVariableTypes';
import { getAttributeInfo } from '../helpers/getAttributeInfo';
import { sellerInformation, itemDetails, customization, shippingAndReturns } from '../pdpMessages';

import { type PdpRespDetailTabs_viewer$key } from './__generated__/PdpRespDetailTabs_viewer.graphql';
import { type PdpRespDetailTabs_user$key } from './__generated__/PdpRespDetailTabs_user.graphql';
import { type PdpRespDetailTabs_item$key } from './__generated__/PdpRespDetailTabs_item.graphql';

import styles from './PdpRespDetailTabs.scss';

const PdpRespPricingGuidanceLazy = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpRespPricingGuidance" */ '../PdpRespDetails/PdpRespPricingGuidance'
        )
);

const CustomTab: FC<{
    name: string;
    type: string;
    dataTn?: string;
    children: ReactNode;
}> = ({ name, type, dataTn, children }) => {
    const { tabCallbackRef, handleClick, isActive } = useTab(name);

    const getTrackingObject = useCallback(
        (action: string) => {
            // Shouldn't ever be used, but adding fallback to identify if unexpected tab type is ever encountered that doesn't exist in label map
            const label = labelMap[type] || `unexpected ${type}`;
            const isInteractive = action === 'clicked';

            return {
                category: 'product',
                action: `pdp tab ${action}`,
                label,
                noninteraction: isInteractive ? 0 : 1,
                eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
                step_interaction_name: isInteractive
                    ? stepInteractionConstants.STEP_INTERACTION_PDP_TAB_CLICKED
                    : stepInteractionConstants.STEP_INTERACTION_PDP_TAB_DISPLAY,
                interaction_type: interactionTypeConstants.INTERACTION_TYPE_PDP,
                trigger: label,
            };
        },
        [type]
    );

    // Track tab impression on mount
    useEffect(() => {
        trackEvent(getTrackingObject('display'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onClick: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement> = useCallback(
        event => {
            handleClick();

            const trackingObject = getTrackingObject('clicked');

            trackEvent(trackingObject, event);
        },
        [handleClick, getTrackingObject]
    );

    return (
        <li className={styles.navTabItem}>
            <Link
                buyerLinkType="linkNoDecoration"
                linkRef={tabCallbackRef}
                className={classnames(styles.navTabLink, { [styles.active]: isActive })}
                onClick={onClick}
                dataTn={dataTn}
            >
                {children}
            </Link>
        </li>
    );
};

const viewerFragment = graphql`
    fragment PdpRespDetailTabs_viewer on Viewer {
        ...PdpRespDealerDetails_viewer
    }
`;

const userFragment = graphql`
    fragment PdpRespDetailTabs_user on User {
        ...PdpRespSharedAboutItemPanel_user
        ...PdpRespDealerDetails_user
        ...PdpRespShippingReturns_user
        ...PdpRespTabsCustomizationPanel_user
        ...PdpSharedBuyerIncentives_user
    }
`;

const itemFragment = graphql`
    fragment PdpRespDetailTabs_item on Item {
        skus {
            serviceId
            ...PdpRespDealerDetails_sku @include(if: $isMultiSku)
            ...PdpRespShippingReturns_sku @include(if: $isMultiSku)
        }
        storyTelling: storyTellingModule(shortTitles: true) {
            tabs {
                tabType
                tabTitle
                ...PdpRespStorytellingTab_storyTellingTab
            }
        }
        seller {
            isAccessible
        }
        isCustomizable
        isDyoCustomizable
        attributes {
            customizable {
                option {
                    serviceId
                }
                text
            }
            ringResizable {
                option {
                    serviceId
                }
            }
        }
        isListingMigrated
        ...PdpRespPricingGuidance_item @include(if: $afterDisplay)
        ...PdpRespSharedAboutItemPanel_item
        ...PdpRespDealerDetails_item
        ...PdpRespShippingReturns_item
        ...PdpRespTabsCustomizationPanel_item
        ...useShowPricingGuidance_item
    }
`;
type TabsState = [string, Dispatch<SetStateAction<string>>];

export const useDetailTabsState = (): TabsState => {
    const state = useState(ITEM_DETAILS);

    return state;
};

export const PdpRespDetailTabs: FC<{
    viewer: PdpRespDetailTabs_viewer$key;
    user: PdpRespDetailTabs_user$key | null;
    item: PdpRespDetailTabs_item$key;
    selectedSkuId: string | null;
    itemQuantity: number | null;
    tabsState: TabsState;
    currency: string;
}> = ({
    selectedSkuId,
    viewer: viewerRef,
    user: userRef,
    item: itemRef,
    itemQuantity,
    tabsState,
    currency,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);

    const showPricingGuidanceGraph = useShowPricingGuidance({ item, currency });

    const tabs = item?.storyTelling?.tabs || [];
    const [activeTab, setActiveTab] = tabsState;
    const relayVariables = useSelector<Store, RelayVariables>(
        state => state.relayVariables.variables
    );
    const { afterDisplay, isMultiSku } = relayVariables;
    const selectedSku = getSelectedSku(item, selectedSkuId);
    const isSellerAccessible = item?.seller?.isAccessible;
    const isListingMigrated = item?.isListingMigrated;
    const isPdpSellerReviewsVariant = usePdpSellerReviewsVariant();

    const { isCustomizable = null, isDyoCustomizable, attributes } = item || {};
    const { customizationOptions = null, isResizable = false } = getAttributeInfo(attributes);

    const showCustomizationOptions =
        (isCustomizable && !!customizationOptions) || isDyoCustomizable || isResizable;

    return (
        <div className={styles.container}>
            <div className={styles.innerContainer}>
                <TabProvider activeTab={activeTab} setActiveTab={setActiveTab}>
                    <div className={styles.tabListWrapper}>
                        <TabList fullWidth customTab indicatorPadding="xSmall">
                            {[
                                [
                                    ITEM_DETAILS,
                                    <CustomTab
                                        key={ITEM_DETAILS}
                                        name={ITEM_DETAILS}
                                        type={ITEM_DETAILS}
                                        dataTn="item-details-tab"
                                    >
                                        {itemDetails}
                                    </CustomTab>,
                                ],
                                ...tabs
                                    .filter(filterNulls)
                                    .map<[string, ReactNode]>((tab, index) => {
                                        const tabKey = `${slugify(tab.tabTitle)}-${index}`;
                                        return [
                                            tabKey,
                                            <CustomTab
                                                key={tabKey}
                                                name={tabKey}
                                                type={tab.tabType}
                                                dataTn={`${tabKey}-tab`}
                                            >
                                                {tab.tabTitle}
                                            </CustomTab>,
                                        ];
                                    }),
                                showCustomizationOptions && [
                                    CUSTOMIZATION,
                                    <CustomTab
                                        key={CUSTOMIZATION}
                                        name={CUSTOMIZATION}
                                        type={CUSTOMIZATION}
                                        dataTn="customization-tab"
                                    >
                                        {customization}
                                    </CustomTab>,
                                ],
                                !isPdpSellerReviewsVariant &&
                                    isSellerAccessible && [
                                        ABOUT_SELLER,
                                        <CustomTab
                                            key={ABOUT_SELLER}
                                            name={ABOUT_SELLER}
                                            type={ABOUT_SELLER}
                                            dataTn="about-the-seller-tab"
                                        >
                                            {sellerInformation}
                                        </CustomTab>,
                                    ],
                                isSellerAccessible &&
                                    !isListingMigrated && [
                                        SHIPPING_RETURNS,
                                        <CustomTab
                                            key={SHIPPING_RETURNS}
                                            name={SHIPPING_RETURNS}
                                            type={SHIPPING_RETURNS}
                                            dataTn="shipping-and-returns-tab"
                                        >
                                            {shippingAndReturns}
                                        </CustomTab>,
                                    ],
                            ]}
                        </TabList>
                    </div>

                    <div className={styles.panelsWrapper}>
                        <PdpSharedStorytellingTrackingContextProvider>
                            <TabPanels renderOnInactive>
                                {[
                                    [
                                        ITEM_DETAILS,
                                        <PdpRespSharedAboutItemPanel
                                            user={user}
                                            item={item}
                                            currency={currency}
                                            selectedSkuId={selectedSkuId}
                                            showHeader={false}
                                            showExpandingArea={activeTab === ITEM_DETAILS}
                                            afterDisplay={afterDisplay}
                                            showThirdCol={showPricingGuidanceGraph}
                                        >
                                            {afterDisplay && (
                                                <Suspense fallback="">
                                                    <PdpRespPricingGuidanceLazy
                                                        currency={currency}
                                                        item={item}
                                                        skuId={selectedSkuId}
                                                    />
                                                </Suspense>
                                            )}
                                        </PdpRespSharedAboutItemPanel>,
                                    ],
                                    ...tabs
                                        .filter(filterNulls)
                                        .map<[string, ReactNode]>((tab, index) => {
                                            const tabKey = `${slugify(tab.tabTitle)}-${index}`;
                                            return [
                                                tabKey,
                                                <PdpSharedStorytellingTrackingWrapper key={tabKey}>
                                                    <PdpRespStorytellingTab
                                                        showHeader={false}
                                                        wrapperClass={styles.storyTellingWrapper}
                                                        storyTellingTab={tab}
                                                        clearBackground={false}
                                                        //Because of the current inactive tab rendering we need this workaraound to make expanding area functional.
                                                        showExpandingArea={activeTab === tabKey}
                                                        dataTn={`${tabKey}-tab-content`}
                                                    />
                                                </PdpSharedStorytellingTrackingWrapper>,
                                            ];
                                        }),
                                    showCustomizationOptions && [
                                        CUSTOMIZATION,
                                        <PdpRespTabsCustomizationPanel
                                            item={item}
                                            user={user || null}
                                        />,
                                    ],
                                    !isPdpSellerReviewsVariant &&
                                        isSellerAccessible && [
                                            ABOUT_SELLER,
                                            <PdpRespDealerDetails
                                                user={user}
                                                item={item}
                                                sku={isMultiSku && selectedSku ? selectedSku : null}
                                                viewer={viewer}
                                                currency={currency}
                                                afterDisplay={afterDisplay}
                                                dataTn="about-the-seller-tab-content"
                                                hideBorder
                                            />,
                                        ],
                                    isSellerAccessible &&
                                        !isListingMigrated && [
                                            SHIPPING_RETURNS,
                                            <PdpRespShippingReturns
                                                currency={currency}
                                                user={user}
                                                item={item}
                                                sku={isMultiSku && selectedSku ? selectedSku : null}
                                                itemQuantity={itemQuantity}
                                                isCondensedTab
                                            />,
                                        ],
                                ]}
                            </TabPanels>
                        </PdpSharedStorytellingTrackingContextProvider>
                    </div>
                </TabProvider>
            </div>
        </div>
    );
};
